import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import i18n from 'scoreboard/components/i18n'
import {
	keys,
	keysInFiscalOrder
} from 'scoreboard/components/months-of-the-year'
import { keys as daysOfTheWeekKeys } from 'scoreboard/components/days-of-the-week'
import RelativeOptions from 'scoreboard/components/relative-options'
import SummaryType from 'scoreboard/datasets/model/summary-type'
const { DAY_OF_THE_WEEK, MONTH_OF_THE_YEAR } = SummaryType
import Calendars from 'scoreboard/components/calendars'
const { DAILY, WEEKLY, MONTHLY, YEARLY } = Calendars

const msg = (idx, key = 'charts.mts.genericPeriodLabel') =>
	_.isString(idx) && _.isEmpty(idx) ? null :
	i18n.get(key,
	// Internal representation of periodIndex is 0-based, but we show the user a
	// 1-based index.
	parseInt(idx) + 1)
const monthMsg = month => i18n.get(`admin.calendar.month.${month}`)
const dayMsg = day => i18n.get(`alerts.${day}`)
//Takes the form [_group-calendar_][_breakdown-calendar_]
const labelGenerators = {
	'yearly': {
		'yearly': idx => msg(idx,'admin.calendar.yearlyN'),
		'quarterly': idx => msg(idx,'admin.calendar.quarterlyN'),
		'monthly': idx => monthMsg(keys[idx]),
		'weekly': idx => msg(idx,'admin.calendar.weeklyN')
	},
	'quarterly': {
		'quarterly': idx => msg(idx,'admin.calendar.quarterlyN'),
		'monthly': idx => msg(idx,'admin.calendar.monthlyN'),
		'weekly': idx => msg(idx,'admin.calendar.weeklyN')
	},
	'monthly': {
		'monthly': idx => msg(idx,'admin.calendar.monthlyN'),
		'weekly': idx => msg(idx,'admin.calendar.weeklyN')
	},
	'fiscal-yearly': {
		'fiscal-yearly': idx => msg(idx,'admin.calendar.usgovernment.yearlyN'),
		'quarterly': idx => msg(idx,'admin.calendar.usgovernment.quarterlyN'),
		'monthly': idx => monthMsg(keysInFiscalOrder[idx]),
		'weekly': idx => msg(idx,'admin.calendar.weeklyN')
	},
	'weekly': {
		'daily': idx => dayMsg(daysOfTheWeekKeys[idx])
	}
}

/**
 * For the given group calendar and breakdown calendar, returns the valid
 * breakdown period options. Handles things like knowing:
 *  - 0 is January for Yearly group, Monthly breakdown
 *  - 0 is October for Fiscal Yearly group, Monthly breakdown
 *  - 0 is "Month 1" for Quarterly group, Monthly breakdown (because the first
 *    month is different for each quarter)
 *
 * @param {String} [summaryType]
 * @param {Int} [groupCalendarId] - ID of the group calendar
 * @param {Int} [breakdownCalendarId] - ID of the breakdown calendar
 *
 * @returns {{id: Int, label: String}[]} - array of id/label objects, usable
 * by dropdown.idLabels()
 */
function breakdownPeriodOptions({
	summaryType,
	groupCalendarId,
	breakdownCalendarId
}) {
	let groupCalendarType, breakdownCalendarType, breakdownPeriodCount

	if (summaryType === DAY_OF_THE_WEEK) {
		groupCalendarType = WEEKLY
		breakdownCalendarType = DAILY
		breakdownPeriodCount = 7
	} else if (summaryType === MONTH_OF_THE_YEAR) {
		groupCalendarType = YEARLY
		breakdownCalendarType = MONTHLY
		breakdownPeriodCount = 12
	} else {
		let groupCalendar = calendars.calendarsById[groupCalendarId]
		let breakdownCalendar = calendars.calendarsById[breakdownCalendarId]

		if (!groupCalendar || !breakdownCalendar) return []

		breakdownPeriodCount = Math.round(
			groupCalendar.avgPeriodSize / breakdownCalendar.avgPeriodSize)

		groupCalendarType = groupCalendar.type
		breakdownCalendarType = breakdownCalendar.type
	}

	return _.range(breakdownPeriodCount).map(index => ({
		id: index,
		label: getBreakdownPeriodLabel({
			groupCalendarType,
			breakdownCalendarType,
			index
		})
	}))
}

/**
 * Returns an appropriate label for a "contextless" breakdown period. Some examples:
 *
 *  - Quarterly group calendar, Monthly breakdown calendar, index 2, returns "Month 2"
 *  - Yearly group calendar, custom breakdown calendar, index 2, returns "Period 2"
 *  - Any group calendar, Monthly breakdown calendar, periodDelta -2, returns "2 months ago"
 *
 * @param {String} groupCalendarType
 * @param {String} breakdownCalendarType
 * @param {Int} [index] the index of the breakdown period within the group period
 * @param {Int} [periodDelta] delta relative to the current period; negative numbers
 * are earlier, positive numbers are later
 * @return {String}
 */
function getBreakdownPeriodLabel({
	groupCalendarType,
	breakdownCalendarType,
	index,
	periodDelta
}) {
	if (!_.isNil(index)) {
		const generatorKey = `${groupCalendarType}.${breakdownCalendarType}`
		const generator = _.get(labelGenerators, generatorKey, msg)
		return generator(index)
	} else if (!_.isNil(periodDelta)) {
		// TODO "1 month ago", "1 quarter later", etc when possible
		return RelativeOptions.label(periodDelta)
	}
}

_.extend(breakdownPeriodOptions, { getBreakdownPeriodLabel })
export default breakdownPeriodOptions
export { getBreakdownPeriodLabel }
breakdownPeriodOptions.__filename__ = 'components/designer/breakdown-period-options.js'
